import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api/vendly';
import styles from './ProfilePage.module.css';

export default function ProfilePage() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handlePw = (e) => setPwForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(''); setError('');
    try {
      await authAPI.update({ name: form.name });
      setMsg('Profile updated successfully!');
    } catch {
      setError('Failed to update profile.');
    } finally { setSaving(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.password !== pwForm.confirm) return setError('Passwords do not match.');
    if (pwForm.password.length < 8) return setError('Password must be at least 8 characters.');
    setSaving(true); setMsg(''); setError('');
    try {
      await authAPI.update({ password: pwForm.password });
      setPwForm({ password: '', confirm: '' });
      setMsg('Password changed successfully!');
    } catch {
      setError('Failed to change password.');
    } finally { setSaving(false); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className={styles.page}>
      <div className={`container ${styles.layout}`}>

        {/* Sidebar */}
        <aside className={styles.sidebar}>
          <div className={styles.avatarWrap}>
            <div className={styles.avatar}>{user?.name?.charAt(0).toUpperCase()}</div>
            <div>
              <p className={styles.avatarName}>{user?.name}</p>
              <p className={styles.avatarEmail}>{user?.email}</p>
            </div>
          </div>

          <nav className={styles.sideNav}>
            {[
              { id: 'profile',  label: 'My profile',      icon: '👤' },
              { id: 'password', label: 'Change password',  icon: '🔒' },
              { id: 'orders',   label: 'Order history',    icon: '📦', link: '/orders' },
              { id: 'seller',   label: 'Switch to seller account', icon: '🏪', link: '/seller/dashboard' },
            ].map(item => item.link ? (
              <Link key={item.id} to={item.link} className={styles.sideLink}>
                <span>{item.icon}</span>{item.label}
              </Link>
            ) : (
              <button
                key={item.id}
                className={`${styles.sideLink} ${tab === item.id ? styles.sideLinkActive : ''}`}
                onClick={() => { setTab(item.id); setMsg(''); setError(''); }}
              >
                <span>{item.icon}</span>{item.label}
              </button>
            ))}
          </nav>

          <button className={styles.logoutBtn} onClick={handleLogout}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Sign out
          </button>
        </aside>

        {/* Main */}
        <main className={styles.main}>
          {msg && <div className={styles.successMsg}>✓ {msg}</div>}
          {error && <div className={styles.errorMsg}>{error}</div>}

          {tab === 'profile' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>My profile</h2>
              <p className={styles.sectionSub}>Update your personal information</p>
              <form className={styles.form} onSubmit={saveProfile}>
                <div className={styles.field}>
                  <label className={styles.label}>Full name</label>
                  <input className={styles.input} name="name" value={form.name} onChange={handle} required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Email address</label>
                  <input className={styles.input} name="email" value={form.email} disabled />
                  <p className={styles.hint}>Email cannot be changed</p>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Member since</label>
                  <input className={styles.input} value={user?.created_at ? new Date(user.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'} disabled />
                </div>
                <button className={styles.saveBtn} type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </form>
            </div>
          )}

          {tab === 'password' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Change password</h2>
              <p className={styles.sectionSub}>Choose a strong password you don't use elsewhere</p>
              <form className={styles.form} onSubmit={savePassword}>
                <div className={styles.field}>
                  <label className={styles.label}>New password</label>
                  <input className={styles.input} type="password" name="password" placeholder="Min. 8 characters" value={pwForm.password} onChange={handlePw} required />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Confirm new password</label>
                  <input className={styles.input} type="password" name="confirm" placeholder="••••••••" value={pwForm.confirm} onChange={handlePw} required />
                </div>
                <button className={styles.saveBtn} type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Update password'}
                </button>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
